import React from "react";
import CompanyHotel from "@app/model/company_hotel/CompanyHotel";
import AlertExecution from "@app/model/alert/AlertExecution";
import {useQuery} from "@tanstack/react-query";
import AlertService from "@app/model/alert/service/AlertService";
import {AlertExecutionQueryKeys} from "@app/model/alert/service/AlertQueryKeys";
import {Link} from "react-router-dom";
import {ChevronLeftIcon} from "@heroicons/react/16/solid";
import Utilities from "@util/Utilities";
import Tag from "@app/component/tag/Tag";
import _isEmpty from "lodash/isEmpty";
import Message from "@app/component/message/Message";
import CompetitorsPriceRateAlertRuleInclusion from "@app/model/alert/CompetitorsPriceRateAlertRuleInclusion";
import CompetitorsPriceRateNotificationsFragment
    from "@app/site/secured/alert-execution/fragment/competitors-price-rate/CompetitorsPriceRateNotificationsFragment";
import CompetitorsPriceRateUpdatedNotificationsFragment
    from "@app/site/secured/alert-execution/fragment/competitors-price-rate/CompetitorsPriceRateUpdatedNotificationsFragment";

const CompetitorsPriceRateAlertExecutionFragment: React.FunctionComponent<{
    companyHotel: CompanyHotel,
    alertExecution: AlertExecution,
    isInDrawer?: boolean
}> = ({companyHotel, alertExecution, isInDrawer}) => {

    const {data: notificationOutputs, isLoading, isSuccess} = useQuery({
        queryFn: () => AlertService.findCompetitorsPriceRateAlertExecutionNotifications(alertExecution.id),
        queryKey: AlertExecutionQueryKeys.notifications(alertExecution.id)
    })

    if (isLoading) {
        return <div className="mx-auto max-w-7xl py-6 px-4 sm:px-6 lg:px-8">
            {[...Array(3)].map((x, i) =>
                <div key={i} className={`${isInDrawer ? 'bg-gray-50' : 'bg-white'} rounded-xl mt-8 p-4 sm:p-8 relative`}>
                    <div className="flex flex-col justify-between h-full">
                        <div className="px-3 py-2">
                            <time dateTime="2024-01-11"></time>
                        </div>
                        <div className="px-3 py-2">
                            <div className="w-full animate-pulse">
                                <div className="h-3.5 bg-gray-200 rounded-full w-8 mb-2.5"></div>
                                <div className="h-2.5 bg-gray-200 rounded-full mb-2"></div>
                                <div className="h-2.5 bg-gray-200 rounded-full mb-2"></div>
                                <div className="h-2.5 bg-gray-200 rounded-full mb-2"></div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    }

    if (isSuccess) {
        const updatedNotificationOutputs = notificationOutputs.filter(o => o.hasAnyNew || o.hasAnyUpdated);

        return <>
            <header className="bg-white shadow-sm">
                <div className="mx-auto max-w-7xl py-4 px-4 sm:px-6 lg:px-8">
                    <div className="md:flex md:items-center md:justify-between">
                        {!isInDrawer && <Link to="/secured/dashboard" className="btn btn-white rounded-full p-0.5 mr-3"
                              title="Go back">
                            <ChevronLeftIcon className="h-4 w-4"/>
                        </Link>}
                        {!!alertExecution && <div className={Utilities.classNames("flex-1 md:flex md:items-center", isInDrawer? "md:justify-end" : "md:justify-between")}>
                            {!isInDrawer && <h1 className="text-lg font-semibold leading-6 text-gray-900">{alertExecution.alertOutput.alert.name} details</h1>}
                            <span
                                className="text-xs text-gray-400">Executed at {Utilities.formatDateString(alertExecution.executionDate, "DD/MM/YYYY HH:mm")}</span>
                        </div>}
                    </div>
                </div>
            </header>

            <main>
                <div className="mx-auto max-w-7xl py-6 px-4 sm:px-6 lg:px-8">
                    {/* Alert details */}
                    {!!alertExecution.alertOutput.alert &&
                        <div className={`${isInDrawer ? 'bg-gray-50' : 'bg-white'} rounded-xl mt-8 p-4 sm:p-8 relative`}>
                            <div>
                                <div className="flex flex-wrap items-center justify-between sm:flex-nowrap">
                                    <h3 className="text-base font-semibold leading-7 text-gray-900">Alert
                                        Configuration</h3>
                                    <Link
                                        to={`/secured/alert/${alertExecution.alertOutput.alert.type}/${alertExecution.alertOutput.alert.id}`}
                                        className="text-sm text-blue-600 font-semibold hover:text-blue-900">Edit</Link>
                                </div>
                                <div className="border-t border-gray-100 pt-5">
                                    <div
                                        className="text-sm leading-6 text-gray-600 grid gap-4 grid-cols-1 md:grid-cols-2 lg:grid-cols-4">
                                        <div className="flex flex-col gap-x-3">
                                            <span>Competitors Rate Change</span>
                                            <span className="text-xs text-gray-400 leading-none">Type</span>
                                        </div>

                                        <div className="flex flex-col gap-x-3">
                                            <span>
                                                Last {alertExecution.alertOutput.competitorsPriceRateAlertRule.pastPeriodInDays} days
                                            </span>
                                            <span className="text-xs text-gray-400 leading-none">Monitoring</span>
                                        </div>
                                        <div className="flex flex-col gap-x-3">
                                            <span>{alertExecution.alertOutput.competitorsPriceRateAlertRule.priceRatePercentage}%</span>
                                            <span
                                                className="text-xs text-gray-400 leading-none">{Utilities.snakeCaseToWords(alertExecution.alertOutput.competitorsPriceRateAlertRule.calculation)}</span>
                                        </div>
                                        <div className="flex flex-col gap-x-3">
                                            <span>{alertExecution.alertOutput.alert.filter.onlyRefundable ? "Lowest Flex" : "Lowest"}</span>
                                            <span className="text-xs text-gray-400 leading-none">Rate</span>
                                        </div>
                                    </div>
                                </div>

                                <div className="mt-6">
                                    <p className="text-sm font-semibold text-gray-800 leading-none mb-2.5">Competitors</p>
                                    <div className="flex flex-wrap gap-x-3 gap-y-3">
                                        {alertExecution.alertOutput.competitors!.map(c => <Tag state="none" key={c.id}>{c.name}</Tag>)}
                                        <Tag state="none" extraClassName={`${isInDrawer ? "bg-white" : "bg-gray-50"}`}>{Utilities.snakeCaseToWords(alertExecution.alertOutput.competitorsPriceRateAlertRule.competitorsInclusion)} competitor{alertExecution.alertOutput.competitorsPriceRateAlertRule.competitorsInclusion === CompetitorsPriceRateAlertRuleInclusion.ALL ? 's' : ''}</Tag>
                                    </div>
                                </div>
                            </div>
                        </div>}
                    {!_isEmpty(updatedNotificationOutputs) && <CompetitorsPriceRateUpdatedNotificationsFragment companyHotel={companyHotel} alertExecution={alertExecution} notificationOutputs={updatedNotificationOutputs} isInDrawer={isInDrawer}/>}
                    {!_isEmpty(notificationOutputs) &&
                        <CompetitorsPriceRateNotificationsFragment companyHotel={companyHotel}
                                                                   alertExecution={alertExecution}
                                                                   notificationOutputs={notificationOutputs}
                                                                   isInDrawer={isInDrawer}/>}

                    {(!alertExecution || _isEmpty(notificationOutputs)) &&
                        <div className="mx-auto max-w-7xl py-6 px-4 sm:px-6 lg:px-8">
                            <Message state="warning">No results found</Message>
                        </div>}
                </div>
            </main>
        </>
    }
    return <></>
}

export default CompetitorsPriceRateAlertExecutionFragment;